//core
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "@front10/landing-page-book/dist/themes/default/index.legacy_browsers.css";
import {
  Switch,
  Route,
  Link,
  useLocation
} from "react-router-dom";
import "./App.css";
import "./site.css";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import PDFDownload from "./components/PDFDownload";
import Pricing from "./components/Pricing";
import GettingStarted from "./components/GettingStarted";
import DecisionReport from "./components/DecisionReport";
import Comparison from "./components/Comparison";
import Privacy from "./components/Privacy";
import NotionTemplate, { NotionConnected } from "./components/NotionTemplate";

import React, { Fragment, useEffect, useState } from "react";

import {
  Header,
  Container,
} from "@front10/landing-page-book/dist/components";

// Internal review pages, routed only when the site is served from this machine.
// Lazy so their code is a separate chunk that production never requests.
const IS_LOCAL = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].indexOf(window.location.hostname) !== -1;
const NotionFlowWalkthrough = IS_LOCAL ? React.lazy(() => import("./components/NotionFlowWalkthrough")) : null;

const ScrollToTop = (props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return <Fragment>{props.children}</Fragment>
};

const App = () => {
  const location = useLocation();
  const isReportRoute = location && location.pathname === '/report';

  const getSubmissionState = (l) => {
    if (!l || !l.search) return { submitted: false, success: false };
    return {
      submitted: l.search.indexOf('submission=') >= 0,
      success: l.search.indexOf('submission=success') >= 0,
    };
  };

  const getRedirectUrl = (l) => {
    if (!l || !l.search) return '#';
    try {
      const params = new URLSearchParams(l.search);
      const team = params.get('team');
      const id = params.get('id');
      if (!team || !id) return '#';
      return `slack://app?team=${team}&id=${id}&tab=home`;
    } catch (e) {
      console.error('Error parsing redirect URL:', e);
      return '#';
    }
  }

  const getManualUrl = (l) => {
    if (!l || !l.search) return '#';
    try {
      const params = new URLSearchParams(l.search);
      const team = params.get('team');
      const id = params.get('id');
      if (!team || !id) return '#';
      return `https://app.slack.com/client/${team}/${id}`;
    } catch (e) {
      console.error('Error parsing manual URL:', e);
      return '#';
    }
  }

  useEffect(() => {
    if (!location || !location.pathname) return;
    
    const pathname = location.pathname;
    
    if (window.gtag && pathname) {
      window.gtag('config', 'G-8B0H6V9WKC', {
        'page_path': pathname
      });
    }
    
    if (pathname && pathname.indexOf("product/install/success") >= 0) {
      const url = getRedirectUrl(location);
      if (url && url !== '#') {
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'G-8B0H6V9WKC',
            'event_category': 'Installation',
            'event_label': 'Slack App Install Success'
          });
        }
        setTimeout(() => {
          if (window.location && window.location.replace) {
            window.location.replace(url);
          }
        }, 1500);
      }
    }
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const HOSTNAME = process.env.REACT_APP_HOSTNAME || '';
  const paymentLink = `${HOSTNAME}/subscription/payment`;
  const trialInstallLink = `${HOSTNAME}/trial`;

  const handleCTAClick = () => {
    if (window.gtag) {
      window.gtag('event', 'click', {
        'event_category': 'CTA',
        'event_label': 'Start Your Free Trial',
        'value': 1
      });
    }
  };

  const SlackMark = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.8 122.8" aria-hidden="true">
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="currentColor"></path>
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="currentColor"></path>
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="currentColor"></path>
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="currentColor"></path>
    </svg>
  );

  const InstallButton = ({ children = 'Add to Slack — free', size }) => (
    <a href={trialInstallLink} className={`dt-btn dt-btn-primary${size === 'sm' ? ' dt-btn-sm' : ''}`} onClick={handleCTAClick}>
      {size !== 'sm' && <SlackMark />}
      {children}
    </a>
  );

  // Real recordings of the product in a clean workspace (see scratchpad
  // record.js / process.sh history). Muted + playsInline so they autoplay on
  // mobile; each only plays while on screen so we never decode four at once.
  // Bump when clips are re-recorded: the edge caches /video/* by URL.
  const CLIP_VERSION = '20260914c';
  const Clip = ({ src, label, where, eager }) => {
    const ref = React.useRef(null);
    useEffect(() => {
      const el = ref.current;
      if (!el) return undefined;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) el.play().catch(() => {});
          else el.pause();
        });
      }, { threshold: 0.4 });
      io.observe(el);
      return () => io.disconnect();
    }, []);
    return (
      <figure className="dt-clip">
        <div className="dt-clip-bar"><b>{where}</b></div>
        <video
          ref={ref}
          poster={`/video/${src}.jpg?v=${CLIP_VERSION}`}
          muted
          loop
          playsInline
          preload={eager ? 'auto' : 'metadata'}
          aria-label={label}
        >
          <source src={`/video/${src}.webm?v=${CLIP_VERSION}`} type="video/webm" />
          <source src={`/video/${src}.mp4?v=${CLIP_VERSION}`} type="video/mp4" />
        </video>
      </figure>
    );
  };

  // Dependency-free scroll reveal. Only armed when JS runs and motion is
  // allowed, so nobody is left looking at invisible content.
  const useReveals = () => {
    useEffect(() => {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
      const els = document.querySelectorAll('.dt-reveal');
      els.forEach((e) => e.classList.add('dt-reveal--armed'));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('dt-reveal--in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      els.forEach((e) => io.observe(e));
      return () => io.disconnect();
    }, []);
  };

  // Tiny line icons for the features grid, drawn on a 24px grid.
  const ICONS = {
    thread: <path d="M4 6h16M4 12h10M4 18h7" />,
    check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    clock: <Fragment><circle cx="12" cy="12" r="8" /><path d="M12 7.5V12l3 2" /></Fragment>,
    target: <Fragment><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /></Fragment>,
    search: <Fragment><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" /></Fragment>,
    chart: <path d="M5 19V11M12 19V5M19 19v-6" />,
    share: <Fragment><path d="M12 4v11" /><path d="M7.5 8.5L12 4l4.5 4.5" /><path d="M5 14v5h14v-5" /></Fragment>,
    home: <Fragment><path d="M4 11l8-6.5 8 6.5" /><path d="M6.5 9.5V19h11V9.5" /></Fragment>,
  };
  const Icon = ({ name }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[name]}</svg>
  );

  // Little four-point sparkles that twinkle around key moments.
  const Sparkle = ({ className }) => (
    <svg className={`dt-sparkle ${className || ''}`} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1.5c.7 5.6 2.9 7.8 8.5 8.5-5.6.7-7.8 2.9-8.5 8.5-.7-5.6-2.9-7.8-8.5-8.5 5.6-.7 7.8-2.9 8.5-8.5z" />
    </svg>
  );

  const Step = ({ when, title, children, points, flip, clip, tone }) => (
    <div className={`dt-step dt-reveal dt-tone--${tone}${flip ? ' dt-step--flip' : ''}`}>
      <div className="dt-step-copy">
        <p className="dt-step-when"><span className="dt-chip">{when}</span></p>
        <h3 className="dt-h3">{title}</h3>
        <p className="dt-body">{children}</p>
        {points && (
          <ul className="dt-step-points">
            {points.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
      </div>
      <div className="dt-stage">
        <Sparkle className="dt-sparkle--a" />
        <Sparkle className="dt-sparkle--b" />
        {clip}
      </div>
    </div>
  );

  const Home = () => {
    useReveals();
    return (
    <div className="dt">
      <section className="dt-hero">
        <div className="dt-wrap dt-hero-grid">
          <div>
            <h1>Decisions made in Slack, <span className="dt-kept">kept<svg className="dt-squiggle" viewBox="0 0 200 18" preserveAspectRatio="none" aria-hidden="true"><path d="M3 12 C 30 3, 52 16, 80 9 S 130 3, 158 10 S 190 12, 197 6" /></svg></span>.</h1>
            <p className="dt-hero-sub">
              Decision Tracker spots decisions as your team makes them, saves them in one click,
              and answers “why did we do this?” before anyone has to ask twice.
            </p>
            <div className="dt-cta-row">
              <InstallButton />
              <a href="#how" className="dt-btn dt-btn-quiet">See it work</a>
            </div>
            {/* Real number, read from the INSTALLS namespace. Keep it honest. */}
            <p className="dt-fineprint">7-day free trial, no card. Then from $3/month per workspace. Installed in 89 workspaces.</p>
          </div>
          <div className="dt-hero-media">
            <p className="dt-note dt-note--hero" aria-hidden="true">
              the real app, no mockups
              <svg viewBox="0 0 60 50" className="dt-note-arrow"><path d="M4 6 C 26 4, 44 14, 46 40" /><path d="M36 32 L 46 42 L 53 30" /></svg>
            </p>
            <div className="dt-stage dt-tone--sky dt-stage--hero">
              <Sparkle className="dt-sparkle--a" />
              <Sparkle className="dt-sparkle--b" />
              <Sparkle className="dt-sparkle--c" />
              <Clip src="recall" where="#backend" label="Someone asks why a past decision was made and Decision Tracker answers with the tracked decision" eager />
            </div>
          </div>
        </div>
      </section>

      <section className="dt-section dt-section--surface" id="how">
        <div className="dt-wrap">
          <div className="dt-section-head dt-reveal">
            <h2 className="dt-h2">The reasoning stays findable after the thread scrolls away</h2>
            <p className="dt-body">
              Teams don’t decide badly. They forget why. Six weeks later the same debate starts
              again and the person who knew has moved on. Every clip here is the real app,
              recorded in Slack.
            </p>
          </div>

          <div className="dt-steps">
            <Step
              when="When it’s decided" tone="blush"
              title="Saved from the message, not written up later"
              points={['Suggests tracking only to the person who wrote it', 'Title pre-filled from what was said', 'Links the conversation, so the why comes with it']}
              clip={<Clip src="capture" where="#billing-team" label="Tracking a decision from a Slack message" />}
            >
              When a message reads like a decision, Decision Tracker offers to save it. One click
              opens the form with the decision and the thread already attached.
            </Step>

            <Step
              flip
              when="Weeks later" tone="sky"
              title="Asked again? It answers with what was decided"
              points={['Replies only to the person asking', 'Shows the reasoning and who made the call', 'Or search any time with /decide find']}
              clip={<Clip src="recall" where="#backend" label="Asking why a past decision was made and getting the tracked answer" />}
            >
              When someone asks about something your team already settled, the decision and its
              reasoning appear right under their question. Nobody gets corrected in public.
            </Step>

            <Step
              when="When it comes up again" tone="mint"
              title="Reopening a topic surfaces what’s already in motion"
              points={['Shows status and what it’s waiting on', 'Deadline reminders at 7 days, 48 and 24 hours', 'Approvals chase the people who owe them']}
              clip={<Clip src="ambient" where="#devops" label="Reconsidering a topic surfaces the related decision in progress" />}
            >
              Question a decision that’s still in progress and you see where it stands before the
              debate starts over.
            </Step>

            <Step
              flip
              when="Every Monday" tone="butter"
              title="One message with everything that needs you"
              points={['Waiting on you, stalled, and decided this week', 'Sends nothing if nothing needs you']}
              clip={<Clip src="digest" where="Decision Tracker" label="The weekly decision digest in Slack" />}
            >
              A short digest in your DMs: what needs your sign-off, what’s gone quiet, and what the
              team settled.
            </Step>
          </div>
        </div>
      </section>

      <section className="dt-section">
        <div className="dt-wrap">
          <div className="dt-section-head dt-reveal">
            <h2 className="dt-h2">Everything included on every plan</h2>
            <p className="dt-body">No feature gates and no add-ons. The $3 plan gets the same product as the $50 one.</p>
          </div>
          <div className="dt-features dt-reveal">
            {[
              ['thread', 'blush', 'Conversation context', 'Attach one or more threads so the reasoning survives, not just the outcome.'],
              ['check', 'mint', 'Approvals', 'Request sign-off, see who has responded, and nudge who hasn’t.'],
              ['clock', 'butter', 'Deadlines', 'Automatic reminders a week, 48 hours and 24 hours before a decision is due.'],
              ['target', 'lilac', 'Outcomes', 'Record what you expected against what happened.'],
              ['search', 'sky', 'Search', 'Find decisions by topic, date or outcome, or ask in any channel.'],
              ['chart', 'blush', 'Analytics', 'Decision volume, time to resolve and success rate.'],
              ['share', 'mint', 'Export and share', 'CSV export with full context and a shareable report for people outside Slack.'],
              ['home', 'butter', 'Home tab', 'Every decision you’re involved in, with status and pending approvals.'],
            ].map(([icon, tone, title, body]) => (
              <div className={`dt-feature dt-tone--${tone}`} key={title}>
                <span className="dt-feature-icon"><Icon name={icon} /></span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dt-section dt-section--surface" id="pricing">
        <div className="dt-wrap">
          <div className="dt-section-head dt-reveal">
            <h2 className="dt-h2">Priced per workspace, not per seat</h2>
            <p className="dt-body">
              Per-seat pricing makes leaving people out the cheapest option, which defeats the point.
              You pay for the size of the team that actually uses it.
            </p>
          </div>
          <div className="dt-tiers dt-reveal">
            {[
              ['1–5 active users', '$3', 'A small team or a single squad.'],
              ['6–20 active users', '$10', 'A department.'],
              ['21+ active users', '$50', 'The whole company.'],
            ].map(([range, price, note]) => (
              <div className="dt-tier" key={range}>
                {price === '$3' && <span className="dt-note dt-note--price" aria-hidden="true">less than a coffee</span>}
                <div className="dt-tier-range">{range}</div>
                <div className="dt-tier-price">{price}<span>/month</span></div>
                <p className="dt-tier-note">{note}</p>
              </div>
            ))}
          </div>
          <div className="dt-terms dt-reveal">
            {[
              ['7 days free', 'No card up front, so nothing is charged if you forget about it.'],
              ['Cancel in one click', 'From the billing page. No email and no notice period.'],
              ['Your data leaves with you', 'Export every decision to CSV at any time, including after you cancel.'],
            ].map(([t, b]) => (
              <div key={t}>
                <h3>{t}</h3>
                <p>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dt-section">
        <div className="dt-wrap dt-faq">
          <h2 className="dt-h2">Questions before you install</h2>
          <div className="dt-faq-list">
            {[
              [
                'Does it read all of our Slack messages?',
                'It reads new messages in public channels it has been added to, so it can suggest tracking and answer recall questions. It doesn’t keep a copy of your history: it stores the decision you save plus a link to the messages you attached, and fetches those live from Slack when you open the decision.',
              ],
              [
                'Will it spam our channels?',
                'No. Suggestions and answers are visible only to the person they’re for. It speaks up uninvited only when someone is reconsidering a tracked topic, and the weekly digest sends nothing if nothing needs you.',
              ],
              [
                'What if my team doesn’t keep it up to date?',
                'There’s no document to maintain. Decisions are saved from messages that already exist, with the title filled in, so it keeps working even if people forget it’s there.',
              ],
              [
                'Does it work in private channels?',
                'Yes, once you invite it. Slack doesn’t let apps add themselves to private channels. In public channels it joins the first time someone uses it there.',
              ],
              [
                'How is this different from a #decisions channel?',
                'A channel still scrolls away and nobody searches it later. Decision Tracker brings the decision back to the person asking, without them needing to know it exists.',
              ],
              [
                'What happens when the trial ends?',
                'Nothing is deleted and nothing is charged, because there’s no card on file. New suggestions pause until you subscribe, and existing decisions stay readable and exportable.',
              ],
            ].map(([q, a]) => (
              <details className="dt-faq-item" key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="dt-section dt-section--surface">
        <div className="dt-wrap dt-close">
          <div>
            <h2 className="dt-h2">Track the next decision your team makes</h2>
            <p className="dt-body">Two-minute install. Free for 7 days, then from $3/month.</p>
            <div className="dt-cta-row"><InstallButton /></div>
          </div>
          <div className="dt-sticky-note" aria-hidden="true">
            <span className="dt-sticky-pin" />
            <p className="dt-sticky-label">Decision</p>
            <p className="dt-sticky-title">Try Decision Tracker this week</p>
            <p className="dt-sticky-meta">Approved by: <b>you</b> <span className="dt-tick">✓</span></p>
          </div>
        </div>
      </section>
    </div>
    );
  };

  const SiteHeader = () => {
    const [open, setOpen] = useState(false);
    return (
      <header className="dt-header">
        <div className="dt-wrap dt-header-inner">
          <Link to="/" className="dt-brand" onClick={() => setOpen(false)}>
            <img src="/images/logo.png" alt="" />
            Decision Tracker
          </Link>
          <button
            type="button"
            className="dt-menu-btn"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
          <nav className={`dt-nav${open ? ' dt-nav--open' : ''}`} onClick={() => setOpen(false)}>
            <Link to="/pricing">Pricing</Link>
            <Link to="/getting-started">Getting started</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/contact">Contact</Link>
            <InstallButton size="sm">Add to Slack</InstallButton>
          </nav>
        </div>
      </header>
    );
  };

  const SiteFooter = () => (
    <footer className="dt-footer">
      <div className="dt-wrap">
        <div className="dt-footer-grid">
          <div>
            <Link to="/" className="dt-brand">
              <img src="/images/logo.png" alt="" />
              Decision Tracker
            </Link>
            <p>Keeps the decisions your team makes in Slack, and the reasons behind them.</p>
          </div>
          <div className="dt-footer-links">
            <div>
              <Link to="/pricing">Pricing</Link>
              <Link to="/getting-started">Getting started</Link>
              <Link to="/notion">Notion template</Link>
              <Link to="/blog">Blog</Link>
            </div>
            <div>
              <Link to="/contact">Contact</Link>
              <Link to="/privacy">Privacy policy</Link>
              <Link to="/terms">Terms</Link>
            </div>
          </div>
        </div>
        <div className="dt-footer-base">© {new Date().getFullYear()} Decision Tracker · decisiontrackerbot@gmail.com</div>
      </div>
    </footer>
  );

    return (
      <Fragment>
        {!isReportRoute && <SiteHeader />}
        <ScrollToTop>
        <Switch>
          <Route exact path="/contact">
            <div className="dt">
              <section className="dt-hero">
                <div className="dt-wrap dt-contact">
                  <div>
                    <h1 className="dt-h2">Get in touch</h1>
                    <p className="dt-body" style={{ marginTop: 16 }}>
                      Feature requests, problems with the app, billing questions — send a note and we’ll
                      reply by email. Or write to <a href="mailto:decisiontrackerbot@gmail.com">decisiontrackerbot@gmail.com</a>.
                    </p>
                  </div>
                  <form action={`${HOSTNAME}/contact`} method="post" className="dt-form">
                    <label htmlFor="contact-email">Email</label>
                    <input id="contact-email" type="email" name="email" required autoComplete="email" />
                    <label htmlFor="contact-message">Message</label>
                    <textarea id="contact-message" name="message" rows="6" required></textarea>
                    <button className="dt-btn dt-btn-primary" type="submit">Send message</button>
                  </form>
                </div>
              </section>
            </div>
          </Route>
        <Route exact path="/blog"><Blog/></Route>
        <Route exact path="/getting-started">
          <GettingStarted trialInstallLink={trialInstallLink} onCTAClick={handleCTAClick}/>
        </Route>
        <Route exact path="/blog/:slug"><BlogPost/></Route>
        <Route exact path="/pdf-download"><PDFDownload/></Route>
        <Route exact path="/report"><DecisionReport/></Route>
        <Route exact path="/comparison"><Comparison/></Route>
        <Route exact path="/"><Home/></Route>
        <Route exact path="/pricing"><Pricing/></Route>
        <Route exact path="/notion">
          <NotionTemplate installLink={trialInstallLink} onCTAClick={handleCTAClick}/>
        </Route>
        <Route exact path="/notion/connected"><NotionConnected location={location}/></Route>
        {IS_LOCAL && (
          <Route exact path="/dev/notion-flow">
            <React.Suspense fallback={<div className="dt" style={{ minHeight: '60vh' }} />}>
              <NotionFlowWalkthrough/>
            </React.Suspense>
          </Route>
        )}
        <Route exact path="/privacy"><Privacy/></Route>
      <Route exact path="/terms">
        <div className="pp"><div className="pp-wrap">
          <h1>Terms of service</h1>
          <p className="pp-lede">
            Our full terms are being finalised. In the meantime, the <Link to="/privacy">privacy policy</Link> explains
            how data is handled, and you can reach us at <a href="mailto:decisiontrackerbot@gmail.com">decisiontrackerbot@gmail.com</a> with any questions.
          </p>
        </div></div>
      </Route>
      <Route exact path="/product/installed">
        <div className="dt">
          <section className="dt-hero dt-page-hero">
            <div className="dt-wrap dt-split">
              <div>
                <h1 className="dt-page-title">You’re in. Decision Tracker is installed.</h1>
                <p className="dt-hero-sub">Taking you to Slack now. If nothing happens, <a href={getManualUrl(location)}>open Slack here</a>.</p>
                <div className="dt-cta-row">
                  <a className="dt-btn dt-btn-primary" href={getManualUrl(location)}>Open Slack</a>
                </div>
              </div>
              <div className="dt-sticky-note dt-sticky-note--steps" aria-label="Next steps">
                <span className="dt-sticky-pin" />
                <p className="dt-sticky-label">Next</p>
                <ol>
                  <li>Right-click a message and choose <b>Track decision</b>. In public channels the app joins by itself.</li>
                  <li>For private channels, type <code>/invite @Decision Tracker</code>.</li>
                  <li>Ask “why did we…?” later and watch it answer.</li>
                </ol>
              </div>
            </div>
          </section>
        </div>
      </Route>
      <Route exact path="/product/install/failure">
        <div className="dt">
          <section className="dt-hero">
            <div className="dt-wrap">
              <h1 className="dt-h2">The install didn’t finish</h1>
              <p className="dt-body" style={{ marginTop: 16 }}>
                Open Slack and check whether Decision Tracker appears under Apps. If it doesn’t,
                try installing again or <a href="/contact">tell us what happened</a>.
              </p>
              <div className="dt-cta-row"><InstallButton>Try installing again</InstallButton></div>
            </div>
          </section>
        </div>
      </Route>
      </Switch>
      </ScrollToTop>
        {!isReportRoute && <SiteFooter />}
      </Fragment>
    );
  }

export default App;



// WEBPACK FOOTER //
// ./src/App.js